
import React from 'react';
import { format } from 'date-fns';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { SignalAccountStatus } from '@/types/signal';
import { Skeleton } from '@/components/ui/skeleton';
import { useTranslation } from 'react-i18next';

interface PremiumCTraderSignalsTableProps {
  signals: SignalAccountStatus[];
  isLoading: boolean;
  currentPage: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

export const PremiumCTraderSignalsTable = ({ 
  signals, 
  isLoading
}: PremiumCTraderSignalsTableProps) => {
  const { t } = useTranslation();
  // Format date for better readability
  const formatDateTime = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM d, HH:mm:ss');
    } catch (e) {
      return dateString;
    }
  };
  
  // Truncate signal ID to first 8 chars
  const truncateId = (id: string) => {
    return id.substring(0, 8);
  };
  
  // Get status badge styling
  const getStatusBadge = (status: string) => {
    switch(status.toLowerCase()) {
      case 'success':
        return "bg-green-500/20 text-green-700 hover:bg-green-500/30 border-green-500/30";
      case 'failed':
        return "bg-red-500/20 text-red-700 hover:bg-red-500/30 border-red-500/30";
      case 'pending':
        return "bg-amber-500/20 text-amber-700 hover:bg-amber-500/30 border-amber-500/30";
      default:
        return "";
    }
  };
  
  // Loading state
  if (isLoading) {
    return (
      <div className="border rounded-md">
        <div className="w-full min-w-[800px]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("signal_id")}</TableHead>
                <TableHead>{t("trading_account")}</TableHead>
                <TableHead>{t("ctrader_profile")}</TableHead>
                <TableHead>{t("volume_sent")}</TableHead>
                <TableHead>{t("order_id")}</TableHead>
                <TableHead>{t("response_time")}</TableHead>
                <TableHead>{t("status")}</TableHead>
                <TableHead>{t("error")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[1, 2, 3, 4, 5].map((i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-12" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  }
  
  // Empty state
  if (signals.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        {t("no_ctrader_signals")}
      </div>
    );
  }
  
  // Render signals table
  return (
    <div className="border rounded-md">
      <div className="w-full min-w-[800px]">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("signal_id")}</TableHead>
              <TableHead>{t("trading_account")}</TableHead>
              <TableHead>{t("ctrader_profile")}</TableHead>
              <TableHead>{t("volume_sent")}</TableHead>
              <TableHead>{t("order_id")}</TableHead>
              <TableHead>{t("response_time")}</TableHead>
              <TableHead>{t("status")}</TableHead>
              <TableHead>{t("error")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {signals.map((signal) => (
              <TableRow key={signal.id}>
                <TableCell className="font-mono text-xs">
                  {truncateId(signal.signal_id)}
                </TableCell>
                <TableCell>{signal.number_account_trading || '—'}</TableCell>
                <TableCell>{signal.csp_account_name || '—'}</TableCell>
                <TableCell>{signal.volume_sent}</TableCell>
                <TableCell className="font-mono text-xs">{signal.order_id || '—'}</TableCell>
                <TableCell>{signal.response_time ? formatDateTime(signal.response_time) : '—'}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={getStatusBadge(signal.status)}>
                    {signal.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs text-red-500 max-w-[200px] truncate">
                  {signal.error_message || '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
